/**
 * The reading column's behaviour: the theme toggle, the mobile map mode, and the section
 * navigation (rail or chips) that follows the scroll. Everything is delegated on the document,
 * so a panel swapped in by HTMX or a language switch needs no re-binding.
 */
import { applyTheme, THEME_KEY } from './theme';

const MAP_MODE_CLASS = 'map-mode';
/** A section counts as current once its top is within this distance of the content's top. */
const SPY_OFFSET = 80;

function content(): HTMLElement | null {
  return document.querySelector('.panel-content');
}

function toggleTheme(): void {
  const theme = document.documentElement.dataset.theme === 'dark' ? 'light' : 'dark';
  applyTheme(theme);
  document.dispatchEvent(new CustomEvent('theme:change'));
  try {
    localStorage.setItem(THEME_KEY, theme);
  } catch {
    // Private mode: the choice lasts the page
  }
}

/** Map mode folds the page to a bar over the map, which stays whole under it (styles.css). */
function setMapMode(on: boolean): void {
  document.documentElement.classList.toggle(MAP_MODE_CLASS, on);
}

/** Whether the home map is in view, no page open: where a port page opens on the map (`followPage`). */
let onHome = !document.querySelector('#panel-container .panel');

/**
 * On a phone, the page shown opens on the page, or, one that asks for the map (`data-map-first`, a port's), on the
 * map when the reader comes from the home map, and in the view they were in otherwise: a port from a route page
 * keeps the page open, a port from a port on the map keeps the map. Its breadcrumb opens at its end, whatever swapped
 * the page in (a link, a language switch).
 */
export function followPage(): void {
  const page = document.querySelector('#panel-container .panel');
  setMapMode(!!page?.matches('[data-map-first]') && (onHome || document.documentElement.classList.contains(MAP_MODE_CLASS)));
  onHome = !page;
  trailToEnd();
}

let currentId = '';

/**
 * The section nav follows the current section, and a region its section too (its link's `data-parent`); a
 * `data-section-link` elsewhere on the page (a tile in the page) only jumps.
 */
function markCurrent(id: string): void {
  if (id === currentId) return;
  currentId = id;
  const links = [...document.querySelectorAll<HTMLElement>('.panel-sections [data-section-link]')];
  const own = links.find((link) => link.dataset.sectionLink === id);
  const parent = links.find((link) => link.dataset.sectionLink === own?.dataset.parent);
  for (const link of links) {
    if (link === own || link === parent) link.setAttribute('aria-current', 'true');
    else link.removeAttribute('aria-current');
  }
  // In view: the region's link, or its section's chip where regions are not shown (a narrow screen)
  (own?.offsetParent ? own : (parent ?? own))?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
}

/** The breadcrumb, where it scrolls (a phone's, styles.css), at its end: the level above the page in view. */
function trailToEnd(): void {
  const trail = document.querySelector<HTMLElement>('.breadcrumb');
  if (!trail) return;
  trail.scrollLeft = trail.scrollWidth;
  markHidden(trail);
}

/** Whether levels of the trail are scrolled out at its start, which its fade then shows. */
function markHidden(trail: HTMLElement): void {
  trail.toggleAttribute('data-more', trail.scrollLeft > 0);
}

let spyLock = 0;

function jumpTo(id: string): void {
  const box = content();
  const target = box?.querySelector<HTMLElement>(`[data-section="${id}"]`);
  if (!box || !target) return;
  const top = target.getBoundingClientRect().top - box.getBoundingClientRect().top + box.scrollTop - 8;
  box.scrollTo({ top });
  markCurrent(id);
  spyLock = Date.now() + 700;
}

function spy(): void {
  if (Date.now() < spyLock) return;
  const box = content();
  if (!box) return;
  const boxTop = box.getBoundingClientRect().top;
  let current = '';
  for (const section of box.querySelectorAll<HTMLElement>('[data-section]')) {
    if (section.getBoundingClientRect().top - boxTop <= SPY_OFFSET) current = section.dataset.section ?? '';
  }
  if (current) markCurrent(current);
}

document.addEventListener('click', (event: MouseEvent) => {
  const target = event.target as HTMLElement;
  if (target.closest('[data-theme-toggle]')) {
    toggleTheme();
    return;
  }
  if (target.closest('[data-map-toggle]')) {
    setMapMode(!document.documentElement.classList.contains(MAP_MODE_CLASS));
    // Unfolded, the trail has a width again
    trailToEnd();
    // The map frames the page again in what the page now leaves (map.ts); a new page frames its own
    document.dispatchEvent(new CustomEvent('ferry:map-mode'));
    return;
  }
  const link = target.closest<HTMLElement>('[data-section-link]');
  if (link) {
    event.preventDefault();
    jumpTo(link.dataset.sectionLink!);
    return;
  }
});

// The content scrolls, not the window; capture catches it wherever the panel is
document.addEventListener(
  'scroll',
  (event) => {
    const target = event.target as HTMLElement;
    if (target.classList?.contains('panel-content')) spy();
    else if (target.classList?.contains('breadcrumb')) markHidden(target);
  },
  true,
);

const isPanel = (event: Event) => {
  const target = (event as CustomEvent<{ target?: Element }>).detail.target;
  return target?.id === 'panel' || target?.id === 'panel-container';
};

// A new page opens on a phone on the map or on the page as it asks (a port's on the map, a route's on the page),
// however it was reached; with the swap, in its view transition, not a beat later. A full load had it from the server (layout.tsx)
document.body.addEventListener('htmx:afterSwap', (event: Event) => {
  if (isPanel(event)) followPage();
});

// A new page starts at its first section
document.body.addEventListener('htmx:afterSettle', (event: Event) => {
  if (isPanel(event)) {
    currentId = '';
    spy();
  }
});
document.addEventListener('ferry:home', () => {
  setMapMode(false);
  onHome = true;
});
document.addEventListener('DOMContentLoaded', () => {
  spy();
  trailToEnd();
});
